import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/nodes';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';

const ITEMS_PER_PAGE = 6;

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || 'ssr-nodes');
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (slug) {
      setActiveTab(slug);
      setPage(1);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeTab);
  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === activeTab),
    [activeTab]
  );
  const totalPages = Math.ceil(filteredArticles.length / ITEMS_PER_PAGE);
  const pagedArticles = filteredArticles.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '分类'} - 免费节点获取指南`,
    description: currentCategory?.description || '',
    url: `https://your-domain.com/category/${activeTab}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>

      <div className="min-h-screen bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-14">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory?.name || '分类' },
            ]}
          />

          <div className="mt-6 mb-8">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
              {currentCategory?.name || '分类'}
            </h1>
            <p className="text-foreground-500 max-w-2xl">{currentCategory?.description}</p>
          </div>

          {/* Category Tabs */}
          <div className="flex gap-2 overflow-x-auto hide-scrollbar mb-10 pb-1">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap shrink-0 ${
                  activeTab === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Article Grid */}
          {pagedArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {pagedArticles.map((article) => (
                <ArticleCard key={article.slug} {...article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无文章，请浏览其他分类</p>
            </div>
          )}

          <Pagination current={page} total={totalPages} onChange={setPage} />
        </div>
      </div>
    </>
  );
}